import { useId, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, Inbox, MessageSquareQuote, Quote, RefreshCw, Search, Sparkles } from 'lucide-react';
import type { InboxAnswer } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { useMailboxes } from '../../hooks/useMailboxes';
import { Button, Card, CardBody, CardHeader, InlineError, Select, Skeleton, Textarea, spring } from '../ui';

const SUGGESTIONS = ['What needs my reply?', 'Which clients are waiting for files?', 'What have most emails been about this week?'];

type Entry =
  | { id: number; question: string; mailboxId: string; state: 'thinking' }
  | { id: number; question: string; mailboxId: string; state: 'result'; answer: InboxAnswer }
  | { id: number; question: string; mailboxId: string; state: 'error'; error: unknown };

const fade = { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -6 }, transition: spring };

export function AskPanel() {
  const mailboxes = useMailboxes().data?.mailboxes ?? [];
  const [question, setQuestion] = useState('');
  const [mailboxId, setMailboxId] = useState('');
  const [history, setHistory] = useState<Entry[]>([]);
  const nextId = useRef(1);
  const qId = useId();
  const mbId = useId();

  const run = async (id: number, q: string, mb: string) => {
    setHistory((h) => {
      const base = { id, question: q, mailboxId: mb, state: 'thinking' as const };
      return h.some((e) => e.id === id) ? h.map((e) => (e.id === id ? base : e)) : [base, ...h];
    });
    try {
      const answer = await api('POST /api/insights/ask', { body: { question: q, mailboxId: mb || undefined } });
      setHistory((h) => h.map((e) => (e.id === id ? { id, question: q, mailboxId: mb, state: 'result', answer } : e)));
    } catch (error) {
      setHistory((h) => h.map((e) => (e.id === id ? { id, question: q, mailboxId: mb, state: 'error', error } : e)));
    }
  };

  const ask = (q: string) => {
    const text = q.trim();
    if (!text) return;
    const id = nextId.current++;
    setQuestion('');
    void run(id, text, mailboxId);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    ask(question);
  };

  const busy = history.some((e) => e.state === 'thinking');
  const mailboxLabel = (id: string) => (id ? mailboxes.find((m) => m.id === id)?.email ?? 'one mailbox' : 'all mailboxes');

  return (
    <Card>
      <CardHeader
        icon={<Sparkles className="h-5 w-5" />}
        title="Ask your inbox"
        description="Answers are built from relevant imported messages only, with links to the emails they came from."
      />
      <CardBody className="space-y-5 pt-5">
        <form onSubmit={onSubmit} className="space-y-3">
          <label htmlFor={qId} className="sr-only">
            Question about your mail
          </label>
          <div className="rounded-2xl border border-line bg-raised/50 p-2 transition-colors focus-within:border-primary focus-within:bg-surface focus-within:ring-2 focus-within:ring-primary/20">
            <Textarea
              id={qId}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Who is waiting on a quote from me?"
              rows={2}
              className="min-h-[56px] !border-0 !bg-transparent !ring-0 focus:!ring-0"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  ask(question);
                }
              }}
            />
            <div className="flex flex-wrap items-center gap-2 px-1 pb-1 pt-1">
              <label htmlFor={mbId} className="sr-only">
                Search in
              </label>
              <Select id={mbId} value={mailboxId} onChange={(e) => setMailboxId(e.target.value)} className="!h-8 w-auto !rounded-lg !bg-surface !pr-8 text-xs">
                <option value="">All mailboxes</option>
                {mailboxes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName ? `${m.displayName} — ${m.email}` : m.email}
                  </option>
                ))}
              </Select>
              {busy && (
                <span id={`${qId}-busy`} className="text-xs text-muted">
                  Still answering an earlier question…
                </span>
              )}
              <Button type="submit" variant="primary" size="sm" icon={<Search className="h-4 w-4" />} disabled={!question.trim()} aria-describedby={busy ? `${qId}-busy` : undefined} className="ml-auto">
                Ask
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1" aria-label="Suggested questions">
            {SUGGESTIONS.map((s) => (
              <button key={s} type="button" onClick={() => ask(s)} className="mp-chip">
                <MessageSquareQuote className="h-3.5 w-3.5" aria-hidden />
                {s}
              </button>
            ))}
          </div>
        </form>

        {history.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-line px-4 py-6 text-center text-[0.8125rem] text-muted">
            Ask a question or pick a suggestion. Your recent questions and answers stay here while this page is open.
          </p>
        ) : (
          <ol className="space-y-3" aria-label="Questions and answers, most recent first">
            {history.map((entry, idx) => (
              <li key={entry.id} className="rounded-2xl border border-hairline bg-raised/40 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-ink">
                    <MessageSquareQuote className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="break-words font-medium text-ink">{entry.question}</p>
                    <p className="text-xs text-muted">Searched {mailboxLabel(entry.mailboxId)}</p>
                  </div>
                </div>
                <div className="mt-4 border-t border-line/60 pt-4" aria-live={idx === 0 ? 'polite' : undefined} aria-busy={entry.state === 'thinking'}>
                  <AnimatePresence mode="wait" initial={false}>
                    {entry.state === 'thinking' && (
                      <motion.div key="thinking" {...fade} className="space-y-2.5">
                        <p role="status" className="flex items-center gap-2 text-[0.8125rem] text-muted">
                          <Bot className="h-4 w-4 animate-pulse text-primary-ink" aria-hidden />
                          Searching your indexed mail…
                        </p>
                        <Skeleton className="h-3.5 w-full" />
                        <Skeleton className="h-3.5 w-11/12" />
                        <Skeleton className="h-3.5 w-3/5" />
                      </motion.div>
                    )}
                    {entry.state === 'error' && (
                      <motion.div key="error" {...fade} className="space-y-2">
                        <InlineError error={entry.error} />
                        <Button size="sm" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void run(entry.id, entry.question, entry.mailboxId)}>
                          Retry
                        </Button>
                      </motion.div>
                    )}
                    {entry.state === 'result' && (
                      <motion.div key="result" {...fade}>
                        <AnswerView answer={entry.answer} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}

function AnswerView({ answer }: { answer: InboxAnswer }) {
  return (
    <div className="space-y-4">
      <p className="reading max-w-[70ch] whitespace-pre-wrap text-ink">{answer.answer}</p>
      {answer.citations.length > 0 && (
        <div>
          <h4 className="mb-2 flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
            <Quote className="h-3.5 w-3.5" aria-hidden /> Sources
          </h4>
          <ul className="grid gap-2 sm:grid-cols-2">
            {answer.citations.map((c, i) => (
              <li key={`${c.messageId}-${i}`}>
                <Link to={`/inbox/${c.threadId}`} className="group flex items-start gap-2.5 rounded-xl border border-hairline bg-surface px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-primary-soft/40">
                  <Inbox className="mt-0.5 h-4 w-4 shrink-0 text-muted group-hover:text-primary-ink" aria-hidden />
                  <span className="min-w-0">
                    <span className="block truncate text-[0.8125rem] font-semibold text-ink">{c.subject || '(no subject)'}</span>
                    {c.quote && <span className="mt-0.5 line-clamp-2 text-[0.75rem] italic leading-relaxed text-muted">“{c.quote}”</span>}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="space-y-1 text-xs text-subtle">
        {answer.coverageNote && <p>{answer.coverageNote}</p>}
        <p>
          Based on {answer.basedOnMessages} message{answer.basedOnMessages === 1 ? '' : 's'} · Generated by {answer.generatedBy}
        </p>
      </div>
    </div>
  );
}
